<template>
  <div class="meal-feedback-panel">
    <!-- Card déclencheuse. Structure alignée sur les cards des dashboards
         (card-header / card-description / card-link) pour une grille homogène.
         Reste un vrai <button> : les styles des dashboards sont scoped, donc
         non héritables, et un <div @click> régresserait l'accessibilité. -->
    <button ref="trigger" type="button" class="feedback-card" :aria-haspopup="'dialog'" @click="openModal">
      <span class="card-header">
        <span class="card-title">🍽️ Mon avis sur le repas du jour</span>
        <span class="badge" :class="badgeClass">{{ badgeLabel }}</span>
      </span>
      <span class="card-description">{{ cardSubtitle }}</span>
      <span class="card-link">{{ cardLinkLabel }} →</span>
    </button>

    <!-- Fenêtre modale -->
    <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
      <div ref="dialog" class="modal-dialog" role="dialog" aria-modal="true" tabindex="-1" :aria-labelledby="titleId">
        <div class="modal-header">
          <h2 :id="titleId" class="modal-title">Mon avis sur le repas du jour</h2>
          <button
            ref="closeButton"
            type="button"
            class="modal-close"
            aria-label="Fermer la fenêtre"
            @click="closeModal"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <p class="modal-context">{{ contextLabel }}</p>

        <div class="modal-content">
          <meal-feedback-form
            :canteen-id="canteenId"
            :service-id="serviceId"
            :date="date"
            @saved="onSaved"
            @existing-feedback="onExistingFeedback"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MealFeedbackForm from "@/components/MealFeedbackForm.vue"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ")

let panelUid = 0

/**
 * Card + fenêtre modale de retour repas, destinée aux dashboards du personnel qui
 * déjeune sur place (SUPERVISOR / WORKER / MANAGER).
 *
 * Le panneau ne choisit pas la cantine : elle lui est passée par le dashboard hôte,
 * qui la tient de `DashboardContextHeader`. Aucun sélecteur ad hoc, et surtout pas de
 * `userCanteenPreviews[0]`, qui prenait arbitrairement la première cantine.
 *
 * RGAA : le focus est piégé dans la modale tant qu'elle est ouverte, elle se ferme au
 * clavier (Échap), et le focus revient sur la card déclencheuse à la fermeture.
 */
export default {
  name: "MealFeedbackPanel",

  components: {
    MealFeedbackForm,
  },

  props: {
    canteenId: {
      type: [Number, String],
      default: null,
    },
    serviceId: {
      type: [Number, String],
      default: null,
    },
    date: {
      type: String,
      default: null,
    },
    serviceLabel: {
      type: String,
      default: "",
    },
  },

  data() {
    panelUid += 1
    return {
      panelId: panelUid,
      isOpen: false,
      hasExistingFeedback: false,
      keyboardTrapAttached: false,
    }
  },

  computed: {
    titleId() {
      return `meal-feedback-dialog-title-${this.panelId}`
    },

    cardSubtitle() {
      if (!this.canteenId || !this.serviceId) return "Sélectionnez un service pour donner votre avis"
      if (this.hasExistingFeedback) return "Avis déjà donné — modifier mon avis"
      return "Donner mon avis en quelques secondes"
    },

    // Badge d'état, à l'image de « Aujourd'hui » / « 138 servis » des autres cards.
    // L'état n'est jamais porté par la seule couleur : le libellé le dit (RGAA).
    badgeLabel() {
      if (!this.canteenId || !this.serviceId) return "Service à choisir"
      return this.hasExistingFeedback ? "Avis donné" : "Aujourd'hui"
    },

    badgeClass() {
      if (!this.canteenId || !this.serviceId) return "badge-grey"
      return this.hasExistingFeedback ? "badge-green" : "badge-blue"
    },

    cardLinkLabel() {
      return this.hasExistingFeedback ? "Modifier mon avis" : "Donner mon avis"
    },

    contextLabel() {
      const parts = []
      if (this.date) parts.push(new Date(this.date).toLocaleDateString("fr-FR"))
      if (this.serviceLabel) parts.push(this.serviceLabel)
      return parts.length ? parts.join(" — ") : "Repas du jour"
    },
  },

  watch: {
    canteenId: "refreshExistingFeedback",
    serviceId: "refreshExistingFeedback",
    date: "refreshExistingFeedback",
  },

  mounted() {
    this.refreshExistingFeedback()
  },

  beforeDestroy() {
    this.releaseBodyScroll()
    this.detachKeyboardTrap()
  },

  methods: {
    async refreshExistingFeedback() {
      if (!this.canteenId || !this.serviceId) {
        this.hasExistingFeedback = false
        return
      }
      try {
        const existing = await this.$store.dispatch("fetchMyMealFeedback", {
          canteenId: this.canteenId,
          date: this.date || new Date().toISOString().split("T")[0],
          serviceId: this.serviceId,
        })
        this.hasExistingFeedback = Boolean(existing)
      } catch {
        this.hasExistingFeedback = false
      }
    },

    openModal() {
      this.isOpen = true
      if (typeof document !== "undefined" && document.body) {
        document.body.style.overflow = "hidden"
      }
      this.attachKeyboardTrap()
      this.$nextTick(() => this.focusFirstElement())
    },

    closeModal() {
      if (!this.isOpen) return
      this.isOpen = false
      this.detachKeyboardTrap()
      this.releaseBodyScroll()
      // RGAA : le focus revient sur l'élément déclencheur.
      this.$nextTick(() => {
        if (this.$refs.trigger && this.$refs.trigger.focus) this.$refs.trigger.focus()
      })
      this.refreshExistingFeedback()
    },

    releaseBodyScroll() {
      if (typeof document !== "undefined" && document.body) {
        document.body.style.overflow = ""
      }
    },

    /**
     * Le clavier est écouté au niveau du document, et non sur l'élément de dialogue :
     * après l'envoi, le formulaire est remplacé par la confirmation, ce qui détruit
     * l'élément focalisé. Le focus retombait alors sur `document.body`, hors du
     * dialogue, et Échap ne fermait plus rien.
     */
    attachKeyboardTrap() {
      if (typeof document === "undefined" || this.keyboardTrapAttached) return
      document.addEventListener("keydown", this.onDocumentKeydown, true)
      this.keyboardTrapAttached = true
    },

    detachKeyboardTrap() {
      if (typeof document === "undefined" || !this.keyboardTrapAttached) return
      document.removeEventListener("keydown", this.onDocumentKeydown, true)
      this.keyboardTrapAttached = false
    },

    onDocumentKeydown(event) {
      if (!this.isOpen) return
      const key = event.key
      // `keyCode` en secours : certains événements synthétiques ne portent que lui.
      if (key === "Escape" || key === "Esc" || event.keyCode === 27) {
        event.preventDefault()
        event.stopPropagation()
        this.closeModal()
      } else if (key === "Tab" || event.keyCode === 9) {
        this.onTabKey(event)
      }
    },

    focusableElements() {
      const dialog = this.$refs.dialog
      if (!dialog || !dialog.querySelectorAll) return []
      // Pas de filtre sur `offsetParent` : la modale ne rend que des éléments visibles
      // (tout est conditionné par `v-if`), et `offsetParent` vaut toujours null hors
      // navigateur, ce qui viderait le piège de focus.
      return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true"
      )
    },

    focusFirstElement() {
      const focusable = this.focusableElements()
      const target = focusable.length ? focusable[0] : this.$refs.dialog
      if (target && target.focus) target.focus()
    },

    /**
     * Piège le focus : Tab depuis le dernier élément revient au premier, et
     * Maj+Tab depuis le premier repart sur le dernier.
     */
    onTabKey(event) {
      const focusable = this.focusableElements()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const dialog = this.$refs.dialog
      const active = typeof document !== "undefined" ? document.activeElement : null
      // Le focus peut avoir quitté le dialogue si l'élément focalisé vient d'être
      // détruit (bascule du formulaire vers la confirmation) : on le rapatrie.
      const activeIsOutside = !active || !dialog || !(dialog === active || dialog.contains(active))

      if (event.shiftKey && (active === first || activeIsOutside)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || activeIsOutside)) {
        event.preventDefault()
        first.focus()
      }
    },

    onExistingFeedback(id) {
      this.hasExistingFeedback = Boolean(id)
    },

    onSaved(payload) {
      this.hasExistingFeedback = true
      this.$emit("saved", payload)
      // Le bouton d'envoi vient d'être détruit avec le formulaire : on replace le
      // focus dans le dialogue pour ne pas le perdre sur `document.body`.
      this.$nextTick(() => {
        if (this.isOpen) this.focusFirstElement()
      })
    },
  },
}
</script>

<style scoped>
/* Le panneau est posé dans la `.cards-grid` des dashboards : il occupe une cellule
   et sa card doit en prendre toute la hauteur pour s'aligner sur ses voisines. */
.meal-feedback-panel {
  height: 100%;
}

/* Valeurs reprises de `.card` des dashboards hôtes : leurs styles sont scoped,
   donc non héritables ici. Toute évolution de `.card` doit être reportée dans ce
   bloc pour que la grille reste homogène. */
.feedback-card {
  /* `display: block` et non flex : les `.card` voisines sont des blocs en flux
     normal, un flex-colonne décalerait le lien et l'alignement du texte. */
  display: block;
  width: 100%;
  height: 100%;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  cursor: pointer;
  /* `inherit` et non `left` : les `.card` voisines héritent l'alignement du
     dashboard, alors qu'un <button> le remet à `center` par défaut. */
  text-align: inherit;
  font: inherit;
}

.feedback-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.feedback-card:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 2px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.card-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #2563eb;
  transition: color 0.2s;
}

.feedback-card:hover .card-link {
  color: #1d4ed8;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}

.badge-green {
  background: #d1fae5;
  color: #065f46;
}

/* Absent des dashboards : état « aucun service choisi », propre à cette card. */
.badge-grey {
  background: #f3f4f6;
  color: #4b5563;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 2000;
}

.modal-dialog {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.25rem;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.modal-close {
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  width: 2.25rem;
  height: 2.25rem;
  font-size: 1rem;
  cursor: pointer;
  color: #1a1a1a;
}

.modal-close:hover {
  background: #e5e7eb;
}

.modal-close:focus-visible {
  outline: 3px solid #1d4ed8;
  outline-offset: 2px;
}

.modal-context {
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0 0 1.25rem 0;
}
</style>
